import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { handleSectionNavigation } from '../utils/navigation';
import { useTheme } from '../hooks/useTheme';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

const { FiMenu, FiX, FiSun, FiMoon, FiWifiOff } = FiIcons;

const NAV_ITEMS = [
  { name: '精神概念', href: 'concept', ariaLabel: '瀏覽 Omakase 精神概念' },
  { name: '四季食材', href: 'seasons', ariaLabel: '探索四季食材' },
  { name: '匠人之道', href: 'craftsmanship', ariaLabel: '了解匠人精神' },
  { name: '體驗指南', href: 'experience', ariaLabel: '查看體驗指南' },
  { name: '深度文章', href: '/articles', ariaLabel: '閱讀深度文章' },
  { name: '常見問題', href: 'faq', ariaLabel: '查看常見問題' }
];

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showOfflineToast, setShowOfflineToast] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { isDark, toggleTheme } = useTheme();
  const isOnline = useOnlineStatus();

  // 監測離線狀態變化並顯示提示
  useEffect(() => {
    if (!isOnline) {
      setShowOfflineToast(true);
      const timer = setTimeout(() => setShowOfflineToast(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  // 🚀 優化：使用 passive 和 requestAnimationFrame 優化滾動性能
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => e.key === 'Escape' && setIsMenuOpen(false);
    const handleClickOutside = (e) => {
      if (isMenuOpen && !e.target.closest('.mobile-menu') && !e.target.closest('.menu-button')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('click', handleClickOutside);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleNavClick = (href) => {
    handleSectionNavigation(href, navigate, isHomePage);
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* 🚀 優化：減少初始動畫時間 */}
      <motion.nav
        initial={{ opacity: 1, y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-xl border-b border-white/20 dark:border-slate-700/50'
            : 'bg-transparent'
        }`}
        role="navigation"
        aria-label="主導航"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <motion.button
              className="flex items-center space-x-3 z-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-rose-500 rounded-lg p-1"
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/')}
              aria-label="返回首頁"
            >
              <motion.div className="relative">
                <motion.div
                  className="w-12 h-12 rounded-full p-0.5 relative"
                  style={{
                    background: `conic-gradient(#ff4757 0deg,#ff6b35 45deg,#ffa502 90deg,#f39c12 135deg,#2ed573 180deg,#1e90ff 225deg,#5352ed 270deg,#a55eea 315deg,#ff4757 360deg)`,
                  }}
                  whileHover={{ scale: 1.08 }}
                  animate={{ rotate: [0, 360] }}
                  transition={{ rotate: { duration: 10, repeat: Infinity, ease: "linear" } }}
                >
                  <div className="w-full h-full bg-gradient-to-br from-white via-blue-50 to-purple-50 dark:from-slate-100 dark:via-blue-100 dark:to-purple-100 rounded-full flex items-center justify-center shadow-lg border border-white/50">
                    <motion.div
                      className="relative w-6 h-3.5"
                      animate={{ scale: [1, 1.08, 1] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      <div className="absolute bottom-0 left-0 w-full h-2 bg-gradient-to-br from-orange-50 via-yellow-50 to-amber-50 rounded-sm border border-orange-200/60"></div>
                      <div className="absolute top-0 left-0 w-full h-2.5 bg-gradient-to-br from-red-400 via-orange-400 to-pink-400 rounded-sm shadow-md border border-red-300/40"></div>
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>
              <div className="flex flex-col">
                <motion.span className="text-xl font-light leading-tight bg-gradient-to-r from-slate-800 via-blue-600 to-purple-600 dark:from-slate-100 dark:via-blue-300 dark:to-purple-300 bg-clip-text text-transparent">
                  お任せ
                </motion.span>
                <span className="text-sm font-light -mt-1 bg-gradient-to-r from-slate-600 via-emerald-500 to-blue-500 dark:from-slate-400 dark:via-emerald-400 dark:to-blue-400 bg-clip-text text-transparent">
                  Omakase
                </span>
              </div>
            </motion.button>
            
            {/* Desktop Navigation - 🚀 移除個別項目的入場動畫 */}
            <div className="hidden lg:flex items-center space-x-6">
              {NAV_ITEMS.map((item) => {
                const isExternal = item.href.startsWith('/');
                return (
                  <div key={item.name}>
                    {isExternal ? (
                      <Link
                        to={item.href}
                        className="relative text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white transition-all font-medium group text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 rounded px-2 py-1"
                        aria-label={item.ariaLabel}
                      >
                        {item.name}
                        <motion.div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-rose-400 via-purple-400 to-blue-400 group-hover:w-full transition-all" />
                      </Link>
                    ) : (
                      <button
                        onClick={() => handleNavClick(item.href)}
                        className="relative text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white transition-all font-medium group text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 rounded px-2 py-1"
                        aria-label={item.ariaLabel}
                      >
                        {item.name}
                        <motion.div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-rose-400 via-purple-400 to-blue-400 group-hover:w-full transition-all" />
                      </button>
                    )}
                  </div>
                );
              })}
              
              {/* 離線狀態指示器 - 只在離線時顯示 */}
              <AnimatePresence>
                {!isOnline && (
                  <motion.div
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-700/50"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    title="離線模式"
                  >
                    <SafeIcon icon={FiWifiOff} className="w-3.5 h-3.5" />
                    <span>離線</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button
                onClick={toggleTheme}
                className="p-2 rounded-full bg-white/10 dark:bg-black/20 backdrop-blur-md shadow-lg hover:shadow-xl transition-all border border-white/20 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-rose-500"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={isDark ? '切換到淺色模式' : '切換到深色模式'}
              >
                <SafeIcon icon={isDark ? FiSun : FiMoon} className="w-4 h-4 text-slate-700 dark:text-slate-200" />
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              className="lg:hidden p-2 rounded-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border border-white/20 dark:border-slate-700/50 z-[60] menu-button focus:outline-none focus:ring-2 focus:ring-rose-500"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label={isMenuOpen ? '關閉選單' : '開啟選單'}
              aria-expanded={isMenuOpen}
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-5 h-5 text-slate-700 dark:text-slate-200" />
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* 離線提示 Toast */}
      <AnimatePresence>
        {showOfflineToast && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-orange-500 text-white px-6 py-3 rounded-full shadow-2xl flex items-center space-x-3"
          >
            <SafeIcon icon={FiWifiOff} className="w-5 h-5" />
            <span className="font-medium">您目前處於離線模式</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55] lg:hidden"
              aria-hidden="true"
            />
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-2xl z-[60] lg:hidden mobile-menu"
              role="dialog"
              aria-modal="true"
              aria-label="行動版選單"
            >
              <div className="flex flex-col h-full pt-20 px-6">
                <motion.div 
                  className="mb-6 space-y-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  {/* 離線狀態 - 行動版 - 只在離線時顯示 */}
                  <AnimatePresence>
                    {!isOnline && (
                      <motion.div 
                        className="flex items-center space-x-3 w-full p-3 rounded-xl border bg-orange-100/50 dark:bg-orange-900/20 border-orange-200/50 dark:border-orange-700/50"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <SafeIcon 
                          icon={FiWifiOff} 
                          className="w-5 h-5 text-orange-700 dark:text-orange-300"
                        />
                        <span className="font-medium text-orange-700 dark:text-orange-300">
                          目前處於離線模式
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button
                    onClick={toggleTheme}
                    className="flex items-center space-x-3 w-full p-3 rounded-xl bg-slate-100/50 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all focus:outline-none focus:ring-2 focus:ring-rose-500"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    aria-label={isDark ? '切換到淺色模式' : '切換到深色模式'}
                  >
                    <SafeIcon icon={isDark ? FiSun : FiMoon} className="w-5 h-5 text-slate-700 dark:text-slate-200" />
                    <span className="text-slate-700 dark:text-slate-200 font-medium">
                      {isDark ? '淺色模式' : '深色模式'}
                    </span>
                  </motion.button>
                </motion.div>

                {/* 🚀 優化：選單項目整體淡入，無個別動畫 */}
                <motion.nav 
                  className="space-y-2" 
                  aria-label="行動版導航"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15, duration: 0.3 }}
                >
                  {NAV_ITEMS.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => handleNavClick(item.href)}
                      className="block w-full text-left text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white transition-colors font-medium py-4 px-4 rounded-xl hover:bg-slate-100/80 dark:hover:bg-slate-800/80 border border-transparent hover:border-slate-200/50 dark:hover:border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-rose-500"
                      aria-label={item.ariaLabel}
                    >
                      <span className="text-lg">{item.name}</span>
                    </button>
                  ))}
                </motion.nav>

                <motion.div 
                  className="mt-auto pb-8 text-center text-slate-500 dark:text-slate-400 text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                >
                  <p>お任せ Omakase</p>
                  <p className="text-xs mt-1">信任的藝術</p>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;